import React, { useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import PageHeader from "components/ui/page-header";
import HasRolePermission from "components/shared/has-role-permission";
import { fetchPatientRecordings, generateSignedUrl } from "actions/admin-recording-url";
import CoachAnalysisControl, { useCoachAnalysis } from "components/coach-analysis/coach-analysis-control";

const TTL_PRESETS = [
  { label: "1 hour", minutes: 60 },
  { label: "24 hours", minutes: 60 * 24 },
  { label: "7 days (max)", minutes: 60 * 24 * 7 }
];

const MAX_TTL_MINUTES = 60 * 24 * 7;

const styles = {
  layout: {
    display: "flex",
    gap: 20,
    flexWrap: "wrap",
    alignItems: "flex-start"
  },
  column: {
    flex: "1 1 420px",
    minWidth: 320
  },
  rightSticky: {
    position: "sticky",
    top: 20
  },
  section: { marginBottom: 20, padding: 16, border: "1px solid #e0e0e0", borderRadius: 4, background: "#fff" },
  label: { display: "block", marginBottom: 4, fontWeight: 600 },
  input: { padding: 6, fontSize: 14, width: "100%", boxSizing: "border-box" },
  button: { padding: "6px 14px", cursor: "pointer" },
  buttonPrimary: { padding: "6px 14px", cursor: "pointer", marginLeft: 8 },
  listScroll: {
    maxHeight: "65vh",
    overflowY: "auto",
    border: "1px solid #eee",
    borderRadius: 4,
    marginTop: 8
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    padding: 6,
    borderBottom: "2px solid #ccc",
    background: "#f7f7f7",
    position: "sticky",
    top: 0,
    zIndex: 1
  },
  td: { padding: 6, borderBottom: "1px solid #eee" },
  row: { cursor: "pointer" },
  selectedRow: { backgroundColor: "#e8f4ff", cursor: "pointer" },
  urlBox: { width: "100%", height: 110, fontFamily: "monospace", fontSize: 12, padding: 6, boxSizing: "border-box" },
  meta: { color: "#666", fontSize: 13, marginTop: 6 },
  placeholder: {
    color: "#888",
    fontStyle: "italic",
    padding: "40px 16px",
    textAlign: "center",
    border: "1px dashed #ccc",
    borderRadius: 4
  }
};

const formatDuration = seconds => {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const inferSource = sid => {
  if (!sid) return "unknown";
  if (sid.startsWith("RE")) return "Twilio";
  return "Synthpop"; // UUID-style
};

const UrlGenerator = () => {
  const dispatch = useDispatch();

  const [patientGuid, setPatientGuid] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [listResult, setListResult] = useState(null); // { patientGuid, patientName, contacts }
  const [selectedContact, setSelectedContact] = useState(null);
  const [ttlMinutes, setTtlMinutes] = useState(TTL_PRESETS[1].minutes); // default 24h
  const [customTtl, setCustomTtl] = useState("");
  const [signLoading, setSignLoading] = useState(false);
  const [signedResult, setSignedResult] = useState(null); // { signedUrl, expiresAtUtc, ... }

  const isTwilioContact = selectedContact && (selectedContact.sRecordingSid || "").startsWith("RE");
  const analysis = useCoachAnalysis({
    contactId: selectedContact?.pkiContact,
    enabled: !!selectedContact && isTwilioContact,
  });

  const onFetchList = async e => {
    e.preventDefault();
    const trimmed = patientGuid.trim();
    if (!trimmed) return;
    setListLoading(true);
    setListResult(null);
    setSelectedContact(null);
    setSignedResult(null);
    try {
      const result = await dispatch(fetchPatientRecordings(trimmed));
      setListResult(result);
    } finally {
      setListLoading(false);
    }
  };

  const onSelectContact = c => {
    setSelectedContact(c);
    setSignedResult(null);
  };

  const onGenerate = async () => {
    if (!selectedContact) return;
    const ttl = customTtl ? parseInt(customTtl, 10) : ttlMinutes;
    if (!ttl || ttl < 1 || ttl > MAX_TTL_MINUTES) {
      window.alert(`TTL must be between 1 and ${MAX_TTL_MINUTES} minutes (7 days).`);
      return;
    }
    setSignLoading(true);
    setSignedResult(null);
    try {
      const result = await dispatch(generateSignedUrl(selectedContact.sRecordingSid, ttl));
      setSignedResult(result);
    } finally {
      setSignLoading(false);
    }
  };

  const onCopy = () => {
    if (!signedResult?.signedUrl) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(signedResult.signedUrl);
    } else {
      const ta = document.getElementById("signed-url-textarea");
      if (ta) {
        ta.select();
        document.execCommand("copy");
      }
    }
  };

  return (
    <HasRolePermission allowedRoles={["Administrator"]}>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: 20 }}>
        <PageHeader title="Recording URL Generator" />
        <p style={styles.meta}>
          Admin-only tool. Enter a patient GUID to see their call recordings, then select one to
          generate a signed URL. URLs are valid up to 7 days.
        </p>

        <div style={styles.layout}>
          {/* LEFT: patient lookup + scrollable contact list */}
          <div style={styles.column}>
            <div style={styles.section}>
              <form onSubmit={onFetchList}>
                <label style={styles.label} htmlFor="patient-guid">Patient GUID</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    id="patient-guid"
                    type="text"
                    style={styles.input}
                    value={patientGuid}
                    onChange={e => setPatientGuid(e.target.value)}
                    placeholder="e.g. 59824ac8ab80a99e4c010cc2"
                    disabled={listLoading}
                  />
                  <button
                    type="submit"
                    style={styles.button}
                    disabled={listLoading || !patientGuid.trim()}>
                    {listLoading ? "Loading…" : "Load"}
                  </button>
                </div>
              </form>
            </div>

            {listResult && (
              <div style={styles.section}>
                <h3 style={{ marginTop: 0, marginBottom: 4 }}>
                  {listResult.patientName || "(unnamed patient)"}
                </h3>
                <p style={styles.meta}>
                  {listResult.contacts.length} recording(s){" "}
                  {listResult.contacts.length > 0 && "· click a row to select"}
                </p>
                {listResult.contacts.length === 0 ? (
                  <p style={styles.meta}>No recordings found for this patient in GCS.</p>
                ) : (
                  <div style={styles.listScroll}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Timestamp</th>
                          <th style={styles.th}>Category</th>
                          <th style={styles.th}>Disposition</th>
                          <th style={styles.th}>Duration</th>
                          <th style={styles.th}>Source</th>
                          <th style={styles.th}>Coach</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listResult.contacts.map(c => {
                          const isSelected = selectedContact?.pkiContact === c.pkiContact;
                          return (
                            <tr
                              key={c.pkiContact}
                              style={isSelected ? styles.selectedRow : styles.row}
                              onClick={() => onSelectContact(c)}>
                              <td style={styles.td}>{moment(c.dTimeStamp).format("YYYY-MM-DD HH:mm")}</td>
                              <td style={styles.td}>{c.sCategory || "—"}</td>
                              <td style={styles.td}>{c.callDisposition || "—"}</td>
                              <td style={styles.td}>{formatDuration(c.durationSeconds)}</td>
                              <td style={styles.td}>{inferSource(c.sRecordingSid)}</td>
                              <td style={styles.td}>{c.requestorName || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: TTL picker + result panel — sticky so it stays in view while scrolling the list */}
          <div style={styles.column}>
            <div style={styles.rightSticky}>
              {!selectedContact && (
                <div style={styles.section}>
                  <div style={styles.placeholder}>
                    Select a contact on the left to generate a signed URL.
                  </div>
                </div>
              )}

              {selectedContact && (
                <div style={styles.section}>
                  <h3 style={{ marginTop: 0 }}>Generate signed URL</h3>
                  <p style={styles.meta}>
                    Recording SID: <code>{selectedContact.sRecordingSid}</code>
                  </p>
                  <p style={styles.meta}>
                    {moment(selectedContact.dTimeStamp).format("YYYY-MM-DD HH:mm")}
                    {selectedContact.sCategory && ` · ${selectedContact.sCategory}`}
                    {selectedContact.callDisposition && ` · ${selectedContact.callDisposition}`}
                  </p>
                  <div style={{ marginTop: 12 }}>
                    <label style={styles.label}>TTL preset</label>
                    {TTL_PRESETS.map(p => (
                      <label key={p.minutes} style={{ marginRight: 16, display: "inline-block" }}>
                        <input
                          type="radio"
                          checked={!customTtl && ttlMinutes === p.minutes}
                          onChange={() => { setTtlMinutes(p.minutes); setCustomTtl(""); }}
                        /> {p.label}
                      </label>
                    ))}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <label style={styles.label} htmlFor="custom-ttl">
                      Or custom (minutes, 1–{MAX_TTL_MINUTES})
                    </label>
                    <input
                      id="custom-ttl"
                      type="number"
                      min="1"
                      max={MAX_TTL_MINUTES}
                      style={{ ...styles.input, width: 140 }}
                      value={customTtl}
                      onChange={e => setCustomTtl(e.target.value)}
                      placeholder="e.g. 30"
                    />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <button style={styles.button} onClick={onGenerate} disabled={signLoading}>
                      {signLoading ? "Generating…" : "Generate signed URL"}
                    </button>
                  </div>
                </div>
              )}

              {selectedContact && isTwilioContact && (
                <div style={styles.section}>
                  <h3 style={{ marginTop: 0 }}>Coach analysis</h3>
                  <CoachAnalysisControl {...analysis} />
                </div>
              )}

              {signedResult?.signedUrl && (
                <div style={styles.section}>
                  <h3 style={{ marginTop: 0 }}>Signed URL</h3>
                  <textarea
                    id="signed-url-textarea"
                    readOnly
                    style={styles.urlBox}
                    value={signedResult.signedUrl}
                    onFocus={e => e.target.select()}
                  />
                  <div style={{ marginTop: 8 }}>
                    <button style={styles.button} onClick={onCopy}>Copy</button>
                    <div style={{ ...styles.meta, marginTop: 8 }}>
                      Expires: {moment(signedResult.expiresAtUtc).format("YYYY-MM-DD HH:mm")} UTC
                    </div>
                    <div style={styles.meta}>
                      Object: <code>{signedResult.objectPath}</code>
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <p style={styles.label}>Spot-check playback</p>
                    <audio controls src={signedResult.signedUrl} style={{ width: "100%" }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </HasRolePermission>
  );
};

export default UrlGenerator;
